import { BALANCE } from '../balance.ts';
import type { Rng } from '../rng.ts';
import type { Direction4, PlayerId, PuzzleAct, PuzzleInstance, Role } from '../types.ts';
import {
  failResult,
  okResult,
  pickHolder,
  shown,
  type ActContext,
  type GenContext,
  type PuzzleEvent,
  type PuzzleModule,
  type SolveInfo,
  type TamperChoice,
  type TickContext,
  type TickOk,
  type ViewContext,
} from './types.ts';
import { alterableClues, clueByKind } from './util.ts';

/**
 * Guarded corridor (GDD §8.7). Turn-based: every player moves their own token one step (or
 * stays); then every guard advances one step along a patrol. A token a guard sees costs
 * alarm. Everyone sees the guards where they stand; only the lookout's radar shows routes.
 */

export interface Cell {
  x: number;
  y: number;
}

export interface CorridorParams {
  w: number;
  h: number;
  walls: Cell[];
  shadows: Cell[];
  exit: Cell;
  starts: Cell[];
  routes: Cell[][];
  vision: number;
}

export interface Token {
  player: PlayerId;
  x: number;
  y: number;
  commit: Direction4 | 'stay' | null;
}

export type GuardMode = 'patrol' | 'divert' | 'return';

export interface GuardState {
  x: number;
  y: number;
  facing: Direction4;
  idx: number;
  mode: GuardMode;
  target: Cell | null;
  roundsLeft: number;
}

export interface CorridorState {
  tokens: Token[];
  guards: GuardState[];
  round: number;
  /** −1 until the first tick/action tells the puzzle what time it is. */
  lastActivityMs: number;
  sightings: number;
}

export const VEC: Record<Direction4, Cell> = {
  n: { x: 0, y: -1 },
  e: { x: 1, y: 0 },
  s: { x: 0, y: 1 },
  w: { x: -1, y: 0 },
};
const DIR4: readonly Direction4[] = ['n', 'e', 's', 'w'];

const SIZE_BY_LEVEL = [5, 5, 5, 6, 6, 7] as const;
const GUARDS_BY_LEVEL = [1, 1, 1, 2, 2, 3] as const;
const SHADOWS_BY_LEVEL = [4, 4, 3, 3, 2, 2] as const;
const WALLS_BY_LEVEL = [2, 2, 3, 4, 5, 6] as const;
export const SOLVE_DEPTH = 36;

const params = (p: PuzzleInstance) => p.params as CorridorParams;
const state = (p: PuzzleInstance) => p.state as CorridorState;

const same = (a: Cell, b: Cell) => a.x === b.x && a.y === b.y;
const key = (par: { w: number }, c: Cell) => c.y * par.w + c.x;

function isWall(par: CorridorParams, c: Cell): boolean {
  return par.walls.some((w) => same(w, c));
}
function inGrid(par: CorridorParams, c: Cell): boolean {
  return c.x >= 0 && c.y >= 0 && c.x < par.w && c.y < par.h;
}
export function walkable(par: CorridorParams, c: Cell): boolean {
  return inGrid(par, c) && !isWall(par, c);
}

function dirOf(from: Cell, to: Cell): Direction4 | null {
  const dx = Math.sign(to.x - from.x);
  const dy = Math.sign(to.y - from.y);
  return DIR4.find((d) => VEC[d].x === dx && VEC[d].y === dy) ?? null;
}

/** BFS first step from `from` toward `target`, or null when already there / unreachable. */
function stepToward(par: CorridorParams, from: Cell, target: Cell): Cell | null {
  if (same(from, target)) return null;
  const prev = new Map<number, Cell | null>();
  prev.set(key(par, from), null);
  const queue: Cell[] = [from];
  for (const cur of queue) {
    if (same(cur, target)) {
      let c = cur;
      let p = prev.get(key(par, c)) ?? null;
      while (p && !same(p, from)) {
        c = p;
        p = prev.get(key(par, c)) ?? null;
      }
      return c;
    }
    for (const d of DIR4) {
      const n = { x: cur.x + VEC[d].x, y: cur.y + VEC[d].y };
      if (!walkable(par, n) || prev.has(key(par, n))) continue;
      prev.set(key(par, n), cur);
      queue.push(n);
    }
  }
  return null;
}

function pathLength(par: CorridorParams, from: Cell, target: Cell): number {
  if (same(from, target)) return 0;
  const dist = new Map<number, number>([[key(par, from), 0]]);
  const queue: Cell[] = [from];
  for (const cur of queue) {
    const d0 = dist.get(key(par, cur)) as number;
    for (const d of DIR4) {
      const n = { x: cur.x + VEC[d].x, y: cur.y + VEC[d].y };
      if (!walkable(par, n) || dist.has(key(par, n))) continue;
      dist.set(key(par, n), d0 + 1);
      if (same(n, target)) return d0 + 1;
      queue.push(n);
    }
  }
  return Number.POSITIVE_INFINITY;
}

/** Cells a guard looks at, and whether a token standing on `cell` is seen. */
export function guardSees(
  par: CorridorParams,
  g: Pick<GuardState, 'x' | 'y' | 'facing'>,
  cell: Cell,
  vision: number,
): boolean {
  if (g.x === cell.x && g.y === cell.y) return true;
  const v = VEC[g.facing];
  const inShadow = par.shadows.some((s) => same(s, cell));
  for (let k = 1; k <= vision; k++) {
    const c = { x: g.x + v.x * k, y: g.y + v.y * k };
    if (!inGrid(par, c) || isWall(par, c)) return false;
    if (same(c, cell)) return !(inShadow && k > 1);
  }
  return false;
}

function visionRange(par: CorridorParams, ctx: { tunnel: boolean; dimVision: boolean }): number {
  return ctx.tunnel || ctx.dimVision ? 1 : par.vision;
}

// ---- Patrols ---------------------------------------------------------------------------

function pingPong(cells: Cell[]): Cell[] {
  if (cells.length <= 2) return cells.slice();
  return [...cells, ...cells.slice(1, -1).reverse()];
}

/** Guard positions at each future round for patrol-only guards (used by the solver). */
export function patrolPositions(par: CorridorParams, rounds: number): GuardState[][] {
  const guards = par.routes.map((route) => initialGuard(route));
  const out: GuardState[][] = [];
  for (let t = 0; t < rounds; t++) {
    guards.forEach((g, i) => {
      patrolStep(par.routes[i] as Cell[], g);
    });
    out.push(guards.map((g) => ({ ...g })));
  }
  return out;
}

function initialGuard(route: Cell[]): GuardState {
  const first = route[0] as Cell;
  const next = route[1] ?? first;
  return {
    x: first.x,
    y: first.y,
    facing: dirOf(first, next) ?? 'e',
    idx: 0,
    mode: 'patrol',
    target: null,
    roundsLeft: 0,
  };
}

function patrolStep(route: Cell[], g: GuardState): void {
  const idx = (g.idx + 1) % route.length;
  const to = route[idx] as Cell;
  const d = dirOf({ x: g.x, y: g.y }, to);
  if (d) g.facing = d;
  g.idx = idx;
  g.x = to.x;
  g.y = to.y;
}

function stepGuard(par: CorridorParams, g: GuardState, route: Cell[]): void {
  const move = (to: Cell): void => {
    const d = dirOf({ x: g.x, y: g.y }, to);
    if (d) g.facing = d;
    g.x = to.x;
    g.y = to.y;
  };
  if (g.mode === 'divert' && g.target) {
    if (g.roundsLeft <= 0) {
      g.mode = 'return';
    } else {
      const next = stepToward(par, { x: g.x, y: g.y }, g.target);
      if (next) move(next);
      g.roundsLeft -= 1;
      return;
    }
  }
  if (g.mode === 'return') {
    const home = route[g.idx] as Cell;
    if (g.x === home.x && g.y === home.y) {
      g.mode = 'patrol';
    } else {
      const next = stepToward(par, { x: g.x, y: g.y }, home);
      if (next) move(next);
      return;
    }
  }
  patrolStep(route, g);
}

// ---- Solver ----------------------------------------------------------------------------

/** Does a sighting-free path exist for a token starting at `start`, given patrol-only guards? */
export function safePathExists(par: CorridorParams, start: Cell, vision: number): boolean {
  const guardsAt = patrolPositions(par, SOLVE_DEPTH);
  let frontier = new Set<number>([key(par, start)]);
  for (let t = 0; t < SOLVE_DEPTH; t++) {
    const next = new Set<number>();
    for (const k of frontier) {
      const cur = { x: k % par.w, y: Math.floor(k / par.w) };
      const options = [cur, ...DIR4.map((d) => ({ x: cur.x + VEC[d].x, y: cur.y + VEC[d].y }))];
      for (const c of options) {
        if (!walkable(par, c)) continue;
        const seen = (guardsAt[t] as GuardState[]).some((g) => guardSees(par, g, c, vision));
        if (seen) continue;
        if (same(c, par.exit)) return true;
        next.add(key(par, c));
      }
    }
    if (next.size === 0) return false;
    frontier = next;
  }
  return false;
}

// ---- Generation ------------------------------------------------------------------------

function generate(ctx: GenContext) {
  const { rng, level } = ctx;
  const size = SIZE_BY_LEVEL[level] ?? 6;
  const nGuards = Math.max(1, (GUARDS_BY_LEVEL[level] ?? 2) + ctx.guardDelta);

  for (let attempt = 0; attempt < 300; attempt++) {
    const exit = { x: rng.int(0, size - 1), y: 0 };
    const starts = rng
      .shuffle(Array.from({ length: size }, (_, x) => ({ x, y: size - 1 })))
      .slice(0, 5);
    const par: CorridorParams = {
      w: size,
      h: size,
      walls: [],
      shadows: [],
      exit,
      starts,
      routes: [],
      vision: 2,
    };
    const taken = (c: Cell) =>
      same(c, exit) || starts.some((s) => same(s, c)) || par.walls.some((w) => same(w, c));

    // Walls in the interior (never on the first or last row).
    for (let i = 0; i < (WALLS_BY_LEVEL[level] ?? 3); i++) {
      const c = { x: rng.int(0, size - 1), y: rng.int(1, size - 2) };
      if (!taken(c)) par.walls.push(c);
    }
    // Patrols: straight segments of 3–5 cells, ping-pong.
    for (let gi = 0; gi < nGuards; gi++) {
      for (let tries = 0; tries < 30; tries++) {
        const horizontal = rng.chance(500);
        const len = rng.int(3, Math.min(5, size));
        const fixed = rng.int(1, size - 2);
        const from = rng.int(0, size - len);
        const cells: Cell[] = Array.from({ length: len }, (_, i) =>
          horizontal ? { x: from + i, y: fixed } : { x: fixed, y: from + i },
        );
        if (cells.some((c) => !walkable(par, c) || same(c, exit))) continue;
        if (par.routes.some((r) => r.some((c) => cells.some((q) => same(q, c))))) continue;
        par.routes.push(pingPong(cells));
        break;
      }
    }
    if (par.routes.length < nGuards) continue;

    // Shadows on free cells.
    const free: Cell[] = [];
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const c = { x, y };
        if (!taken(c)) free.push(c);
      }
    }
    par.shadows = rng.shuffle(free).slice(0, SHADOWS_BY_LEVEL[level] ?? 3);

    // Every start must connect to the exit and have a sighting-free path.
    const vision = par.vision;
    if (!par.starts.every((s) => pathLength(par, s, exit) < Number.POSITIVE_INFINITY)) continue;
    if (!par.starts.every((s) => safePathExists(par, s, vision))) continue;

    const radarHolder = pickHolder(ctx.roles, ['lookout', 'mastermind']);
    const players = ctx.players.filter((pl) => !pl.expelled);
    const st: CorridorState = {
      tokens: players.map((pl, i) => ({
        player: pl.id,
        x: (par.starts[i] as Cell).x,
        y: (par.starts[i] as Cell).y,
        commit: null,
      })),
      guards: par.routes.map((r) => initialGuard(r)),
      round: 0,
      lastActivityMs: -1,
      sightings: 0,
    };
    return {
      params: par,
      state: st,
      clues: [{ id: 'radar', kind: 'corridorRadar', holder: radarHolder, value: par.routes }],
      // Everyone moves; the lookout also has gadgets.
      solvers: ['mastermind', 'locksmith', 'hacker', 'lookout', 'driver'] as Role[],
    };
  }
  throw new Error('corridor generator failed');
}

// ---- Rounds ----------------------------------------------------------------------------

function allCommitted(par: CorridorParams, st: CorridorState): boolean {
  return st.tokens.every((t) => t.commit !== null || same(t, par.exit));
}

function resolveRound(
  par: CorridorParams,
  st: CorridorState,
  ctx: { tunnel: boolean; dimVision: boolean },
): { sighted: boolean; solved: boolean; seenPlayers: PlayerId[] } {
  for (const t of st.tokens) {
    if (t.commit && t.commit !== 'stay') {
      const n = { x: t.x + VEC[t.commit].x, y: t.y + VEC[t.commit].y };
      if (walkable(par, n)) {
        t.x = n.x;
        t.y = n.y;
      }
    }
    t.commit = null;
  }
  st.guards.forEach((g, i) => {
    stepGuard(par, g, par.routes[i] as Cell[]);
  });
  const vision = visionRange(par, ctx);
  const seenPlayers = st.tokens
    .filter((t) => st.guards.some((g) => guardSees(par, g, t, vision)))
    .map((t) => t.player);
  st.round += 1;
  const solved = st.tokens.every((t) => same(t, par.exit));
  return { sighted: seenPlayers.length > 0, solved, seenPlayers };
}

function roundEvents(
  r: { sighted: boolean; seenPlayers: PlayerId[] },
  st: CorridorState,
): PuzzleEvent[] {
  const events: PuzzleEvent[] = [{ type: 'corridor.round', data: { round: st.round } }];
  if (r.sighted) {
    events.push({ type: 'corridor.sighted', data: { players: r.seenPlayers, round: st.round } });
  }
  return events;
}

function apply(p: PuzzleInstance, act: PuzzleAct, ctx: ActContext) {
  const par = params(p);
  const st = state(p);
  const me = st.tokens.find((t) => t.player === ctx.actor);

  if (act.kind === 'corridor.move') {
    if (!me) return failResult('notAllowed', 'you have no token here');
    if (!['n', 'e', 's', 'w', 'stay'].includes(act.dir))
      return failResult('invalid', 'bad direction');
    if (act.dir !== 'stay') {
      const n = { x: me.x + VEC[act.dir].x, y: me.y + VEC[act.dir].y };
      if (!walkable(par, n)) return failResult('illegalMove', 'there is a wall there');
    }
    me.commit = act.dir;
    st.lastActivityMs = ctx.execMs;
    if (!allCommitted(par, st)) return okResult({});
    const r = resolveRound(par, st, ctx);
    return okResult({
      solved: r.solved,
      errorTenths: r.sighted ? BALANCE.penalty.corridor : 0,
      sighted: r.sighted,
      undo: r.sighted ? { kind: 'none' } : null,
      events: roundEvents(r, st),
    });
  }

  if (act.kind === 'corridor.coin' || act.kind === 'corridor.petard') {
    const actor = ctx.players.find((pl) => pl.id === ctx.actor);
    if (ctx.actorRole !== 'lookout' || !actor) {
      return failResult('notAllowed', 'only the lookout has diversions');
    }
    const target = { x: act.x, y: act.y };
    if (!walkable(par, target)) return failResult('illegalMove', 'cannot throw there');
    if (act.kind === 'corridor.coin') {
      if (actor.coins <= 0) return failResult('noCharges', 'no coins left');
      st.lastActivityMs = ctx.execMs;
      let n = 0;
      for (const g of st.guards) {
        if (pathLength(par, { x: g.x, y: g.y }, target) <= BALANCE.coinRangeSteps) {
          g.mode = 'divert';
          g.target = target;
          g.roundsLeft = BALANCE.coinDivertRounds;
          n++;
        }
      }
      return okResult({
        consumes: 'coin',
        events: [{ type: 'corridor.coin', data: { x: act.x, y: act.y, guards: n } }],
      });
    }
    if (actor.petards <= 0) return failResult('noCharges', 'no firecracker left');
    st.lastActivityMs = ctx.execMs;
    for (const g of st.guards) {
      g.mode = 'divert';
      g.target = target;
      g.roundsLeft = BALANCE.petardDivertRounds;
    }
    return okResult({
      consumes: 'petard',
      fixedTenths: BALANCE.fixed.petard,
      events: [{ type: 'corridor.petard', data: { x: act.x, y: act.y } }],
    });
  }
  return failResult('invalid', 'not a corridor action');
}

function tick(p: PuzzleInstance, _dt: number, ctx: TickContext): TickOk {
  const par = params(p);
  const st = state(p);
  const none: TickOk = { events: [], errorTenths: 0, sighted: false, solved: false };
  if (st.lastActivityMs < 0) {
    st.lastActivityMs = ctx.execMs;
    return none;
  }
  if (ctx.execMs - st.lastActivityMs < BALANCE.corridorIdleMs) return none;
  // Idle players stay put and the round resolves.
  for (const t of st.tokens) if (t.commit === null) t.commit = 'stay';
  st.lastActivityMs = ctx.execMs;
  const r = resolveRound(par, st, ctx);
  return {
    events: roundEvents(r, st),
    errorTenths: r.sighted ? BALANCE.penalty.corridor : 0,
    sighted: r.sighted,
    solved: r.solved,
  };
}

function view(p: PuzzleInstance, ctx: ViewContext): Record<string, unknown> {
  const par = params(p);
  const st = state(p);
  const clue = clueByKind(p, 'corridorRadar');
  const base: Record<string, unknown> = {
    type: 'corridor',
    solved: p.solved,
    w: par.w,
    h: par.h,
    walls: par.walls,
    shadows: par.shadows,
    exit: par.exit,
    round: st.round,
    tokens: st.tokens.map((t) => ({
      player: t.player,
      x: t.x,
      y: t.y,
      committed: t.commit !== null,
    })),
    // Everyone sees where the guards are and which way they face, never the route.
    guards: st.guards.map((g) => ({ x: g.x, y: g.y, facing: g.facing })),
    vision: ctx.tunnel ? 1 : par.vision,
    clues: {},
  };
  if (clue.holder === ctx.role && !ctx.tunnel) {
    const routes = shown(clue) as Cell[][];
    base['radar'] = {
      routes,
      next: routes.map((r, i) => {
        const idx = (st.guards[i] as GuardState).idx;
        return [1, 2, 3, 4].map((k) => r[(idx + k) % r.length] as Cell);
      }),
    };
  }
  if (ctx.role === 'lookout') {
    const me = ctx.players.find((pl) => pl.id === ctx.playerId);
    base['gadgets'] = { coins: me?.coins ?? 0, petards: me?.petards ?? 0 };
  }
  if (ctx.revealed) base['secret'] = { routes: par.routes };
  return base;
}

function legalActs(p: PuzzleInstance, role: Role, ctx: ActContext): PuzzleAct[] {
  if (p.solved) return [];
  const par = params(p);
  const st = state(p);
  const out: PuzzleAct[] = [];
  const me = st.tokens.find((t) => t.player === ctx.actor);
  if (me && !same(me, par.exit)) {
    out.push({ kind: 'corridor.move', dir: 'stay' });
    for (const d of DIR4) {
      if (walkable(par, { x: me.x + VEC[d].x, y: me.y + VEC[d].y })) {
        out.push({ kind: 'corridor.move', dir: d });
      }
    }
  }
  if (role === 'lookout') {
    const actor = ctx.players.find((pl) => pl.id === ctx.actor);
    for (let y = 0; y < par.h; y++) {
      for (let x = 0; x < par.w; x++) {
        if (!walkable(par, { x, y })) continue;
        if ((actor?.coins ?? 0) > 0) out.push({ kind: 'corridor.coin', x, y });
        if ((actor?.petards ?? 0) > 0) out.push({ kind: 'corridor.petard', x, y });
      }
    }
  }
  return out;
}

function tamper(p: PuzzleInstance, rng: Rng, exclude: Role | null): TamperChoice | null {
  if (alterableClues(p, exclude).length === 0) return null;
  const par = params(p);
  const clue = clueByKind(p, 'corridorRadar');
  const routes = (clue.value as Cell[][]).map((r) => r.map((c) => ({ ...c })));
  const ri = rng.int(0, routes.length - 1);
  const route = routes[ri] as Cell[];
  const ci = rng.int(0, route.length - 1);
  const c = route[ci] as Cell;
  const moves = DIR4.map((d) => ({ x: c.x + VEC[d].x, y: c.y + VEC[d].y })).filter((n) =>
    walkable(par, n),
  );
  route[ci] = moves.length > 0 ? rng.pick(moves) : c;
  return { clueId: clue.id, value: routes };
}

function solve(raw: unknown): SolveInfo {
  const par = raw as CorridorParams;
  const ok = par.starts.every((s) => safePathExists(par, s, par.vision));
  return { solvable: ok, difficulty: par.routes.length * 10 + par.walls.length };
}

function autoSolve(p: PuzzleInstance): void {
  const par = params(p);
  for (const t of state(p).tokens) {
    t.x = par.exit.x;
    t.y = par.exit.y;
  }
}

export const corridorModule: PuzzleModule = {
  type: 'corridor',
  generate,
  apply,
  tick,
  view,
  legalActs,
  tamper,
  autoSolve,
  undo: () => undefined,
  solve,
};
